import { action, observable } from "mobx";
import { FormulaFormVM } from "../template/FormulaFormVM";
import MessageCenter from "cd-eft/components/common/MessageCenter/MessageCenterVM";
import { HydraulicFormulaController } from "cd-eft/services/eftSetApi";
import { PumpDrainageRequestDto } from "cd-eft/_codegen_/openapi-generator/eft-set";
import { convertStringFractionToDecimal } from "cd-eft/utils/mathUtilities";

interface IPumpDrainageForm {
  drainedArea: string,
  drainageCoeffs: number,
  saftyFactors: number,
  pumpCyclesPerHours: number,
  inletElev: string,
  outletElev: string,
  startLevel: string,
  sumpArea?: string,
  solveFor: boolean,
  stopLevel?: string
}
interface IPumpDrainageResults {
  flowQ?: string;
  dimensions?: string;
  stopLevel?: string,
  sumpArea?: string,
  errorResponse?: {
    errorCode?: string;
    errorMessage?: string;
    errorType?: string;
  };
  validationPass?: boolean;
}


export class PumpDrainageVM extends FormulaFormVM {
  readonly formLabelsForExport;
  readonly resultLabelsForExport;
  constructor(props) {
    super(props);
    this.formLabelsForExport = {
      drainedArea: "Drained Area (ac)",
      drainageCoeffs: "Drainage Coefficient (in/ day)",
      saftyFactors: "Safety Factor",
      pumpCyclesPerHours: "Pump Cycles (per hour)",
      inletElev: "Inlet Elevation (ft)",
      outletElev: "Outlet Elevation (ft)",
      startLevel: "Start Level (ft)",
      solveFor: "Solve For",
      sumpArea: "Sump Area (sq ft)",
      stopLevel: "Stop Level (ft)",
    }
    this.resultLabelsForExport = {
      flowQ: 'Capacity Q (cfs)',
      sumpArea: 'Sump Area (sq ft)',
      stopLevel: "Stop Level (ft)",
      dimensions: 'Sump Dimensions (ft diameter or ft squared)'
    }
    // Any Form specific contructor code. Otherwise, remove contructor - use parent
  }

  // Expand with onChange handlers, onBlur validation, Submit handler, on submit validation, individual variables, etc.
  @observable pumpDrainageForm: IPumpDrainageForm = {
    drainedArea: "0",
    drainageCoeffs: 0,
    saftyFactors: 0,
    pumpCyclesPerHours: 0,

    inletElev: "0",
    outletElev: "0",
    startLevel: "0",
    solveFor: true,
    sumpArea: "0",
    stopLevel: "0",
  }
  @observable pumpDrainageResults: IPumpDrainageResults = {
    flowQ: '',
    dimensions: '',
    stopLevel: '',
    sumpArea: '',
  }
  @observable pumpDrainageFormInputSnapshot: IPumpDrainageForm | undefined = undefined
  @observable pumpDrainageFormResultSnapshot: IPumpDrainageResults | undefined = undefined
  @observable hasResults: boolean = false;
  @observable drainageCoeffsOptions: any = [];
  @observable saftyFactorsOptions: any = [];
  @observable pumpCyclesPerHoursOptions: any = [];
  @observable solveForControl: number = 1;

  @observable formValidation = {
    hasError: true,
    drainedArea: false,
    drainageCoeffs: false,
    saftyFactors: false,
    pumpCyclesPerHours: false,
    outletElev: false,
    startLevel: false,
    solveFor: false,
    sumpArea: false,
    stopLevel: false,
    inletElev: false,
    formErrors: {
      drainedArea: '',
      drainageCoeffs: '',
      saftyFactors: '',
      pumpCyclesPerHours: '',
      outletElev: '',
      startLevel: '',
      solveFor: '',
      sumpArea: '',
      inletElev: '',
      stopLevel: ''
    },
  };

  @action.bound handleOnKeyDown(event) {
    ['e', 'E', '+', '-'].includes(event.key) && event.preventDefault();
  }

  @action.bound PumpDrainageErrorClass(field) {
    const errorMessage = this.formValidation.formErrors[field];
    return errorMessage?.length === 0 ? '' : ' usa-input--error';
  }
  get stopControl() {
    return this.solveForControl === 0;
  }
  get sumpControl() {
    return this.solveForControl === 1;
  }
  @action.bound setFixedValue(name, fixed) {
    const event1 = {
      target: {
        name: name,
        value: this.roundingHelperString(fixed, 2)
      }
    }
    this.handleInputChange(event1, true);
  }
  @action.bound async handleInputChange(event, within?: boolean) {
    const { name, value } = event.target;
    if (!within && value.length > 7) {
      return
    }
    switch (name) {
      case 'drained-area':
        this.pumpDrainageForm.drainedArea = value;
        break;
      case 'drainage-coeffs':
        this.pumpDrainageForm.drainageCoeffs = value;
        break;
      case 'safty-factors':
        this.pumpDrainageForm.saftyFactors = value;
        break;
      case 'pump-cycles-perHours':
        this.pumpDrainageForm.pumpCyclesPerHours = value;
        break;
      case 'inlet-elev':
        this.pumpDrainageForm.inletElev = value;
        break;
      case 'outlet-elev':
        this.pumpDrainageForm.outletElev = value;
        break;
      case 'start-level':
        this.pumpDrainageForm.startLevel = value;
        break;
      case 'sump-area':
        this.pumpDrainageForm.sumpArea = value;
        break;
      case 'stop-level':
        this.pumpDrainageForm.stopLevel = value;
        break;
      case 'stopControl':
        this.solveForControl = 0
        this.pumpDrainageForm.solveFor = false;
        break;
      case 'sumpControl':
        this.solveForControl = 1
        this.pumpDrainageForm.solveFor = true;
        break;
    }
  }
  @action.bound async validateInput(name, value) {
    const val = this.roundingHelperNumber(parseFloat(value), 2);
    if (name !== 'drainage-coeffs' && name !== 'safty-factors' && name !== 'pump-cycles-perHours' && !isNaN(value) && value !== '') {
      this.setFixedValue(name, val);
    }

    switch (name) {
      case 'drained-area':
        if (value === '' || val <= 0 || Number.isNaN(value)) {
          this.formValidation.drainedArea = true;
          this.formValidation.formErrors.drainedArea = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', [], 'Drained Area');
        }
        else {
          this.formValidation.drainedArea = false;
          this.formValidation.formErrors.drainedArea = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'drainage-coeffs':
        if (value === '' || Number.isNaN(value)) {
          this.formValidation.drainageCoeffs = true;
          this.formValidation.formErrors.drainageCoeffs = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_26');
        }
        else {
          this.formValidation.drainageCoeffs = false;
          this.formValidation.formErrors.drainageCoeffs = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'safty-factors':
        if (value === '' || Number.isNaN(value)) {
          this.formValidation.saftyFactors = true;
          this.formValidation.formErrors.saftyFactors = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_26')
        }
        else {
          this.formValidation.saftyFactors = false;
          this.formValidation.formErrors.saftyFactors = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'pump-cycles-perHours':
        if (value === '' || Number.isNaN(value)) {
          this.formValidation.pumpCyclesPerHours = true;
          this.formValidation.formErrors.pumpCyclesPerHours = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_26');
        }
        else {
          this.formValidation.pumpCyclesPerHours = false;
          this.formValidation.formErrors.pumpCyclesPerHours = '';
          this.formValidation.hasError = false;
        }
        break;

      case 'inlet-elev':
        if (value === '' || val <= 0 || Number.isNaN(value)) {
          this.formValidation.inletElev = true;
          this.formValidation.formErrors.inletElev = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', [], 'Inlet Elevation')
        }
        else {
          this.formValidation.inletElev = false;
          this.formValidation.formErrors.inletElev = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'outlet-elev':
        if (value === '' || val <= 0 || Number.isNaN(value)) {
          this.formValidation.outletElev = true;
          this.formValidation.formErrors.outletElev = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', [], 'Outlet Elevation');
        }
        else {
          this.formValidation.outletElev = false;
          this.formValidation.formErrors.outletElev = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'start-level':
        if (value === '' || val <= 0 || Number.isNaN(value)) {
          this.formValidation.startLevel = true;
          this.formValidation.formErrors.startLevel = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', [], 'Start Level')
        }
        else {
          this.formValidation.startLevel = false;
          this.formValidation.formErrors.startLevel = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'sump-area':
        if (value === '' || val < 0 || Number.isNaN(value)) {
          this.formValidation.sumpArea = true;
          this.formValidation.formErrors.sumpArea = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', [], 'Sump Area')
        }
        else {
          this.formValidation.sumpArea = false;
          this.formValidation.formErrors.sumpArea = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'stop-level':
        if (value === '' || val < 0 || Number.isNaN(value)) {
          this.formValidation.stopLevel = true;
          this.formValidation.formErrors.stopLevel = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', [], 'Stop Level')
        }
        else {
          this.formValidation.stopLevel = false;
          this.formValidation.formErrors.stopLevel = '';
          this.formValidation.hasError = false;
        }
        break;
    }
  }
  @action async validateReqInput() {
    await this.validateInput("drained-area", this.pumpDrainageForm.drainedArea);
    await this.validateInput("drainage-coeffs", this.pumpDrainageForm.drainageCoeffs);
    await this.validateInput("safty-factors", this.pumpDrainageForm.saftyFactors);
    await this.validateInput("pump-cycles-perHours", this.pumpDrainageForm.pumpCyclesPerHours);
    await this.validateInput("inlet-elev", this.pumpDrainageForm.inletElev);
    await this.validateInput("outlet-elev", this.pumpDrainageForm.outletElev);
    await this.validateInput("start-level", this.pumpDrainageForm.startLevel);
  }
  get validateRequiredInputs() {
    if (!this.formValidation.drainedArea && !this.formValidation.drainageCoeffs && !this.formValidation.saftyFactors &&
      !this.formValidation.pumpCyclesPerHours && !this.formValidation.inletElev && !this.formValidation.outletElev && !this.formValidation.startLevel) {
      return true
    }
    return false
  }

  @action  async computePumpDrainage() {
    this.disableComputeBtn = true;
    this.disablePrintBtn = true;
    this.isLoading = true
    MessageCenter.blockCalls([])
    await this.validateReqInput();
    if (this.validateRequiredInputs) {
      try {
        const requestPayload: PumpDrainageRequestDto = {
          drainageCoeffs: this.pumpDrainageForm.drainageCoeffs,
          drainedArea: parseInt(this.pumpDrainageForm.drainedArea),
          pumpCyclesPerHours: this.pumpDrainageForm.pumpCyclesPerHours,
          pumpDrainageElevationDto: {
            inletElev: parseInt(this.pumpDrainageForm.inletElev),
            outletElev: parseInt(this.pumpDrainageForm.outletElev),
            startLevel: parseInt(this.pumpDrainageForm.startLevel),
            stopLevel: this.pumpDrainageForm.stopLevel ? parseInt(this.pumpDrainageForm.stopLevel) : 0.00,
            sumpArea: this.pumpDrainageForm.sumpArea ? parseInt(this.pumpDrainageForm.sumpArea) : 0.00
          },
          saftyFactors: this.pumpDrainageForm.saftyFactors,
          solveFor: this.pumpDrainageForm.solveFor
        }
        const response = await HydraulicFormulaController.calculatePumpDrainageUsingPOST(requestPayload);
        const shortCode = response?.data?.errorResponse?.errorCode?.replace(/[123]_[a-zA-Z]+_[a-zA-Z]+_/, '');
        if (response && response.data) {
          if (!response.data.errorResponse) {
            this.hasResults = true;
            this.pumpDrainageResults = {
              flowQ: response.data.flowQ,
              stopLevel: response.data.stopLevel,
              sumpArea: response.data.sumpArea,
              dimensions: response.data?.dimensions?.match(/\d{1,5}\.\d{1,2}/g)?.join(" or ")
            }
            this.pumpDrainageFormInputSnapshot = {
              drainedArea: this.pumpDrainageForm.drainedArea,
              drainageCoeffs: this.pumpDrainageForm.drainageCoeffs,
              saftyFactors: this.pumpDrainageForm.saftyFactors,
              pumpCyclesPerHours: this.pumpDrainageForm.pumpCyclesPerHours,
              inletElev: this.pumpDrainageForm.inletElev,
              outletElev: this.pumpDrainageForm.outletElev,
              startLevel: this.pumpDrainageForm.startLevel,
              ...(this.pumpDrainageForm.solveFor ? { stopLevel: this.pumpDrainageForm.stopLevel } : { sumpArea: this.pumpDrainageForm.sumpArea }),
              solveFor: this.pumpDrainageForm.solveFor,
            },
              this.pumpDrainageFormResultSnapshot = {
                flowQ: response.data.flowQ,
                ...(this.pumpDrainageForm.solveFor ? { sumpArea: response.data.sumpArea } : { stopLevel: response.data.stopLevel }),
                dimensions: response.data?.dimensions?.match(/\d{1,5}\.\d{1,2}/g)?.join(" or ")
              }
          } else if (
            response?.data?.errorResponse?.errorMessage != undefined &&
            response?.data?.errorResponse?.errorType != undefined
          ) {
            MessageCenter.addNotification(
              response?.data?.errorResponse?.errorMessage,
              response?.data?.errorResponse?.errorType,
              shortCode
            );
            this.hasResults = false;
            this.disableComputeBtn = true;
          }
        }
      } catch (error) {
        console.error(error);
      }
      finally {
        this.disablePrintBtn = false;
      }
    } else {
      MessageCenter.resetMessageCenter();
      MessageCenter.unblockCalls()
      await MessageCenter.addNotificationFromCode('Common_26');
      this.hasResults = false;
    }
    MessageCenter.unblockCalls();
    this.disableComputeBtn = false;
    this.isLoading = false
  }

  @action.bound launchAndExport() {
    this.disablePrintBtn = true;
    this.exportedInputs = [];
    this.exportedResults = [];
    for (const input in this.pumpDrainageFormInputSnapshot) {
      const label = this.formLabelsForExport[input]
      const value = this.pumpDrainageFormInputSnapshot[input];
      if (input === "solveFor") {
        continue;
      } else {
        this.exportedInputs.push({
          label: label,
          value: value,
        });
      }
    }
    for (const result in this.pumpDrainageFormResultSnapshot) {
      this.exportedResults.push({
        label: this.resultLabelsForExport[result],
        value: this.pumpDrainageFormResultSnapshot[result]
      })
    }
    this.printFormula();
    this.disablePrintBtn = false;
  }

  @action.bound initializeDefaultValues(): void {
    this.pumpDrainageForm = {
      drainedArea: this.roundingHelperString(this.defaultValues.drainedArea, 2),
      drainageCoeffs: this.defaultValues.drainageCoeffs,
      saftyFactors: this.defaultValues.saftyFactors,
      pumpCyclesPerHours: this.defaultValues.pumpCyclesPerHours,
      solveFor: this.defaultValues.solveFor,
      inletElev: this.roundingHelperString(this.defaultValues.pumpDrainageElevationDto.inletElev, 2),
      outletElev: this.roundingHelperString(this.defaultValues.pumpDrainageElevationDto.outletElev, 2),
      startLevel: this.roundingHelperString(this.defaultValues.pumpDrainageElevationDto.startLevel, 2),
      sumpArea: this.roundingHelperString(this.defaultValues.pumpDrainageElevationDto.sumpArea, 2),
      stopLevel: this.roundingHelperString(this.defaultValues.pumpDrainageElevationDto.stopLevel, 2),
    }
    this.drainageCoeffsOptions = this.defaultValues.drainageCoeff ? (this.defaultValues.drainageCoeff as string[]).map((value, index) => ({
      value: convertStringFractionToDecimal(value),
      name: value,
    })) : null;
    this.saftyFactorsOptions.push({ name: "1.00", value: 1.0 }, { name: "1.20", value: 1.2 })
    this.pumpCyclesPerHoursOptions.push({ name: "6", value: 6.00 }, { name: "8", value: 8.00 }, { name: "10", value: 10 }, { name: "12", value: 12 })
    this.pumpDrainageFormInputSnapshot = { ...this.pumpDrainageForm }
  }
}